export const getAlphaColor = (hexColor: string, alpha: number) => {
  const alphaHex = Number((alpha * 255).toFixed(0)).toString(16);
  return `${hexColor}${alphaHex.length === 1 ? `0${alphaHex}` : alphaHex}`;
};

export const getColorWithoutAlpha = (hexAlphaColor: string) => {
  return hexAlphaColor.slice(0, -2);
};

const getRelativeLuminance = (channel: number) => {
  const normalizedChannel = channel / 255;
  return normalizedChannel <= 0.03928
    ? normalizedChannel / 12.92
    : ((normalizedChannel + 0.055) / 1.055) ** 2.4;
};

export const getContrastTextColor = (hexColor: string) => {
  const normalizedColor = hexColor.replace(/^#/, '');

  if (!/^(?:[\da-f]{6}|[\da-f]{8})$/i.test(normalizedColor)) {
    return '#111827';
  }

  const alpha =
    normalizedColor.length === 8
      ? parseInt(normalizedColor.slice(6, 8), 16) / 255
      : 1;
  const channels = [0, 2, 4].map(
    (offset) =>
      parseInt(normalizedColor.slice(offset, offset + 2), 16) * alpha +
      255 * (1 - alpha),
  );
  const [red, green, blue] = channels;
  const luminance =
    0.2126 * getRelativeLuminance(red) +
    0.7152 * getRelativeLuminance(green) +
    0.0722 * getRelativeLuminance(blue);
  const whiteContrast = 1.05 / (luminance + 0.05);
  const darkTextLuminance =
    0.2126 * getRelativeLuminance(0x11) +
    0.7152 * getRelativeLuminance(0x18) +
    0.0722 * getRelativeLuminance(0x27);
  const darkContrast = (luminance + 0.05) / (darkTextLuminance + 0.05);

  return whiteContrast >= darkContrast ? '#FFFFFF' : '#111827';
};

export const getRgbaColor = (hexColor: string, alpha: number) => {
  // 8자리 hex color (알파값 포함) 정규식
  const hexAlphaRegex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;
  // 6자리 hex color 정규식
  const hexRegex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;

  let match;

  // 8자리 hex color 처리 (#RRGGBBAA)
  if ((match = hexAlphaRegex.exec(hexColor))) {
    const [_, r, g, b, a] = match;
    const alphaValue = parseInt(a, 16) / 255;
    return `rgba(${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(
      b,
      16,
    )}, ${alphaValue})`;
  }

  // 6자리 hex color 처리 (#RRGGBB)
  if ((match = hexRegex.exec(hexColor))) {
    const [_, r, g, b] = match;
    return `rgba(${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(
      b,
      16,
    )}, ${alpha})`;
  }

  return modifyRgbaColorOpacity(hexColor, alpha);
};

export function modifyRgbaColorOpacity(
  colorString: string,
  targetOpacity: number,
) {
  // rgba 정규식
  const rgbaRegex =
    /rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*([0-9.]+)\s*\)/;
  // rgb 정규식
  const rgbRegex = /rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)/;

  let match;

  // rgba 문자열 처리
  if ((match = colorString.match(rgbaRegex))) {
    const [_, r, g, b] = match;
    return `rgba(${r}, ${g}, ${b}, ${targetOpacity})`;
  }

  // rgb 문자열 처리
  if ((match = colorString.match(rgbRegex))) {
    const [_, r, g, b] = match;
    return `rgba(${r}, ${g}, ${b}, ${targetOpacity})`;
  }

  // 유효하지 않은 포맷
  throw new Error(`Invalid color string format : ${colorString}`);
}
